import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, BookOpen, Clock, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { AppShell } from '@/components/layout/AppShell';
import { ArticleBodyRenderer } from '@/components/learn/ArticleBodyRenderer';
import { QuizCtaPanel } from '@/components/quiz/QuizCtaPanel';
import { articlesService } from '@/services/articles.service';
import { ApiError } from '@/services/http';
import { topicById, type LearnsTopicId } from '@/constants/learns-data';
import type { ArticleView } from '@/types/api';

/**
 * ArticlePage — `/learns/articles/:slug`. Minimal reader view that
 * fetches the live article by slug and renders its markdown via the
 * shared MarkdownRenderer. Drafts resolve to a 404 for public callers
 * because the server hides them behind the same status filter.
 *
 * Scope for this pass:
 *   - Title, topic badge, meta row, author byline, sanitized body.
 *   - Back-to-Learns link at the top.
 *   - No glossary pop-ups, no quiz, no comments — those are tracked as
 *     follow-up features in the Learns spec.
 */
export function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [article, setArticle] = useState<ArticleView | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (!slug) {
      setNotFound(true);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setNotFound(false);
    (async () => {
      try {
        const row = await articlesService.getBySlug(slug);
        if (cancelled) return;
        setArticle(row);
      } catch (err) {
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 404) {
          setNotFound(true);
        } else {
          toast.error(err instanceof ApiError ? err.message : 'Could not load the article.');
          setNotFound(true);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [slug]);

  if (loading) {
    return (
      <AppShell mode="kid" className="mx-auto w-full max-w-[780px] px-8 py-10">
        <p className="text-center text-or-slate-500">Loading…</p>
      </AppShell>
    );
  }

  if (notFound || !article) {
    return (
      <AppShell mode="kid" className="mx-auto w-full max-w-[780px] px-8 py-16">
        <div className="text-center">
          <h1 className="font-display text-[32px] font-bold text-or-slate-900">
            Article not found
          </h1>
          <p className="mt-2 text-or-slate-600">This article may have been unpublished or moved.</p>
          <button
            type="button"
            onClick={() => navigate('/learns')}
            className="mt-6 inline-flex items-center gap-1.5 rounded-full border border-or-slate-200 bg-white px-4 py-2 text-[13px] font-semibold text-or-slate-700 hover:bg-or-slate-50"
          >
            <ArrowLeft className="h-4 w-4" aria-hidden />
            Back to Ollie Learns
          </button>
        </div>
      </AppShell>
    );
  }

  const topic = topicById(article.topic as LearnsTopicId);
  const TopicIcon = topic?.icon;

  return (
    <AppShell mode="kid" className="mx-auto w-full max-w-[780px] px-6 pb-24 pt-6">
      {/* Back link */}
      <Link
        to="/learns"
        className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-or-slate-500 hover:text-or-slate-700"
      >
        <ArrowLeft className="h-3.5 w-3.5" aria-hidden />
        Ollie Learns
      </Link>

      {/* Hero image — admin-uploaded thumbnail rendered edge-to-edge at
          the top of the reader. Hidden entirely when no thumbnail is set
          so articles without art collapse cleanly to the title block.
          16:9 keeps the crop wide enough to feel cinematic without
          dominating the page; `object-cover` preserves the focal point
          on tall or narrow source images. */}
      {article.thumbnailImage ? (
        <div
          className="mt-5 overflow-hidden rounded-2xl bg-or-cream-50"
          style={{ aspectRatio: '16 / 9' }}
        >
          <img
            src={article.thumbnailImage}
            alt=""
            aria-hidden
            className="h-full w-full object-cover"
          />
        </div>
      ) : null}

      {/* Header */}
      <header className="mt-5 flex flex-col gap-3">
        {topic ? (
          <span
            className="inline-flex w-fit items-center gap-1.5 rounded-full px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.1em]"
            style={{ backgroundColor: topic.soft, color: topic.accent }}
          >
            {TopicIcon ? (
              <TopicIcon width={12} height={12} strokeWidth={2} color={topic.accent} />
            ) : null}
            {topic.label}
          </span>
        ) : null}

        <h1
          style={{
            fontFamily: '"DM Serif Display", serif',
            fontWeight: 400,
            fontSize: '42px',
            lineHeight: 1.08,
            letterSpacing: '-0.01em',
            color: '#1F1B16',
            margin: 0,
          }}
        >
          {article.title}
        </h1>

        {article.summary ? (
          <p
            style={{
              fontFamily: 'Inter, sans-serif',
              fontSize: '16px',
              lineHeight: 1.55,
              color: '#3D342A',
              margin: 0,
            }}
          >
            {article.summary}
          </p>
        ) : null}

        {/* Meta row */}
        <div
          className="flex flex-wrap items-center text-or-slate-500"
          style={{ gap: '14px', fontFamily: 'Inter, sans-serif', fontSize: '12px' }}
        >
          <Meta>
            <Clock style={metaIconStyle} strokeWidth={2} aria-hidden />
            {article.mins} min read
          </Meta>
          <Meta>
            <BookOpen style={metaIconStyle} strokeWidth={2} aria-hidden />
            Grade {article.level}
          </Meta>
          {article.author ? (
            <Meta>
              <Sparkles style={metaIconStyle} strokeWidth={2} aria-hidden />
              {article.author}
            </Meta>
          ) : null}
        </div>
      </header>

      {/* Body */}
      <article className="mt-8 border-t border-or-slate-200 pt-6">
        <ArticleBodyRenderer html={article.body} />
      </article>

      {/* Quiz CTA — self-gates when no live quiz exists */}
      <QuizCtaPanel parentKind="article" parentKey={article.slug} />

      {/* Footer back link */}
      <div className="mt-10 border-t border-or-slate-200 pt-6">
        <Link
          to="/learns"
          className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-or-slate-700 hover:text-or-slate-900"
        >
          <ArrowLeft className="h-4 w-4" aria-hidden />
          Back to Ollie Learns
        </Link>
      </div>
    </AppShell>
  );
}

const metaIconStyle: React.CSSProperties = {
  width: '12px',
  height: '12px',
  color: '#8A7F72',
  flexShrink: 0,
};

function Meta({ children }: { children: React.ReactNode }) {
  return (
    <span className="inline-flex items-center" style={{ gap: '6px' }}>
      {children}
    </span>
  );
}
